import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, DSSetup, MetricItem, PopupData, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive, toSorted } from 'webapp/utils/common';
import { initialMetricSort, sortByConnectionStatus } from 'webapp/utils/metric-sort';

import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FilterLineComponent from 'webapp/components/filter/line';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import FormMetricSelectPopupListComponent from 'webapp/components/form/metric-select/popup/list';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type {
	CustomMetric,
	CustomQuery,
	HorizonMetric,
	Metric,
	Script,
} from 'webapp/models-custom';
import FilterSortMetricSelect from 'webapp/models-custom/filter-sort/metric-select';
import type SourceType from 'webapp/models-custom/source-type';
import { SourceOption } from 'webapp/models-custom/source-type';

import type { onConnectSourceResult } from 'webapp/services/connect';

export type { MetricItem };

export default class PopupMetricSelectPopupComponent extends PopupInnerModalComponent<PopupData.Form.MetricSelect> {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('analytics') declare analyticsService: Service.Analytics;
	@tracked items: MetricItem[] = [];
	@tracked filterSort: FilterSortMetricSelect;

	// incremental load
	@tracked shownItems: MetricItem[] = [];
	currentPage = 1;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.setupOptions();

		this.filterSort = new FilterSortMetricSelect(
			{
				getAllItems: () => {
					return this.items;
				},
				afterFilter: () => {
					// always reset to page 1 after filtering
					this.incrementalLoad(1);
					next(this, this.reposition);
					return this.shownItems;
				},
				additionalSorts: [sortByConnectionStatus],
			},
			{
				skipConnected: this.popupData.skipConnected,
				filterSortSourceOptions: this.popupData.filterSortSourceOptions,
				isItemsMetricForClient: this.isItemsMetricForClient,
			},
		);
	}

	@cached
	get selectedFilterSourceType(): SourceType | null {
		const selectedFilters = this.filterSort?.findFilter(1)?.selected as string[],
			selectedFilter = selectedFilters[0]!;

		// selected source type is only handled for single selection
		if (selectedFilters.length > 1) {
			return null;
		}

		let sourceType = this.mmSourceTypesService.findModelByKey(selectedFilter);

		if (!sourceType) {
			const sourceTypeKey = this.mmSourcesService.getSourceTypeKey(selectedFilter);

			if (sourceTypeKey) {
				sourceType = this.mmSourceTypesService.findModelByKey(sourceTypeKey);
			}
		}

		return sourceType || null;
	}

	get showCreateCustomMetrics() {
		return !this.navigationService.isMobileApp && !this.popupData.skipCreateCustomMetrics;
	}

	get isItemsMetricForClient() {
		return !!this.popupData.customClientMetrics?.length;
	}

	get selectedMetricCategoryFilter(): string[] | undefined {
		return this.filterSort?.filters[0]!.selected as string[] | undefined;
	}

	get hideNoResultsActions(): boolean {
		if (this.popupData.hideNoResultsActions) {
			return true;
		}

		const selectedFilters = this.selectedMetricCategoryFilter;
		if (selectedFilters?.length !== 1) {
			return false;
		}

		const selectedSource = this.mmSourcesService.findModel(selectedFilters[0]!);
		if (selectedSource?.selectedSourceOption === SourceOption.ABSTRACT) {
			return true;
		}

		const sourceType = this.selectedFilterSourceType;
		return !!sourceType && !sourceType.isQBSupported && !sourceType.isExcel;
	}

	@action
	setupOptions(): void {
		let metricItems: MetricItem[] = this.mmMetricsService.metrics;

		const clientMetrics = this.popupData.customClientMetrics;
		if (clientMetrics && !this.popupData.hideOptionToSelectDimensions) {
			metricItems = clientMetrics ?? [];
		}

		if (this.popupData.disableNotConnected) {
			metricItems.filter((item) => {
				if (!item.isOnSource) {
					item.isTemporaryDisabledForMetricSelectPopupNotConnected = true;
				}
			});
		}

		if (this.popupData.skipConnected) {
			metricItems = metricItems.filter((item) => item.isGeneric);
		}

		if (this.popupData.skipGoals) {
			metricItems = metricItems.filter((item) => !item.isTypeGoal);
		}

		if (this.popupData.skipCustomGenerics) {
			metricItems = metricItems.filter((item) => !item.isGenericCustom);
		}

		// TODO: Remove once horizon metrics are supported as calculated metric inputs
		if (this.popupData.skipHorizon) {
			metricItems = metricItems.filter((item) => !item.isTypeHorizon);
		}

		if (this.popupData.disableYouTubeMetrics) {
			metricItems.forEach((item) => {
				if (item.sourceType?.isYouTube) {
					item.isTemporaryDisabledForMetricSelectPopupYoutube = true;
				}
			});
		}

		const selected = this.popupData.metric,
			selectedId = selected?.id;
		// don't show deprecated metrics in list unless they are selected
		metricItems = metricItems.filter((item) => {
			if (item.isDeprecated) {
				return selectedId && item.id === selectedId;
			}
			return true;
		});

		// when selected item, show as first item on list together with other metrics in its source group
		if (selected) {
			const sourceTypeKey = selected.dataSourceTypeKey;

			// get all metrics from same source group
			let metricsSourceGroup = selected.spaceAccessId
				? metricItems.filter((m) => m.spaceAccessId === selected.spaceAccessId)
				: metricItems.filter(
						(metric) => metric.isGeneric && metric.dataSourceTypeKey === sourceTypeKey,
					);
			const selectedMetricsSourceGroupItem = metricsSourceGroup.find(
				(m) => m.id === selected.id,
			);
			if (metricsSourceGroup.length && selectedMetricsSourceGroupItem) {
				// move selected metric to first position
				metricsSourceGroup = [
					selectedMetricsSourceGroupItem,
					...metricsSourceGroup.filter((item) => item.id !== selected.id),
				];

				// move source group to the beginning of all options
				const metricsSourceGroupIds = metricsSourceGroup.map((m) => m.id);
				metricItems = [
					...metricsSourceGroup,
					...metricItems.filter((item) => !metricsSourceGroupIds.includes(item.id)),
				];
			}
		}

		this.items = toSorted(metricItems, initialMetricSort);
	}

	@action
	incrementalLoad(page?: number, pageSize = 20): void {
		const filterSortResults = this.filterSort?.results;

		if (filterSortResults) {
			const sortedResults = [...filterSortResults];
			const currentPage = page || this.currentPage + 1;

			const oldShownItems = currentPage === 1 ? [] : this.shownItems;

			this.currentPage = currentPage;
			this.shownItems = [
				...oldShownItems,
				...sortedResults.slice((currentPage - 1) * pageSize, currentPage * pageSize - 1),
			];
		}
	}

	@action
	selectMetricData(metricData: DSSetup.MetricData): void {
		const metric = this.mmMetricsService.getMetricForKeyAndSpaceAccessId(
			metricData.key,
			metricData.spaceAccessId,
		);
		if (metric) {
			this.selectItem(metric);
		}
	}

	@action
	selectItem(item: MetricItem, e?: Event): void {
		const target = e?.target as HTMLElement | null,
			isConnectClick = !!target?.classList?.contains('action--connect');

		if (item.mustResolveBeforeSelecting) {
			const metric = item as Metric;
			if (metric.spaceAccessId) {
				// click on whole metric does the same as clicking on "Create" button
				this.handleResolvingPredefinedMetric(metric, metric.spaceAccessId);
			} else if (!this.popupData.hideConnectBtn) {
				this.connectSource(metric);
			}
		} else if (isConnectClick) {
			// user specifically clicked on "Connect" button
			this.connectSource(item as Metric);
		} else if (!item.isTemporaryDisabledForMetricSelectPopup) {
			this.resolveAndClose(item.metricSetupData!);
		}
	}

	@action
	createCustomMetric(): void {
		this.mmMetricsService.openCreateNewCustomMetricPopup(
			'metric_select',
			(model: CustomMetric) => {
				this.setupOptions();
				this.incrementalLoad(1);

				if (model.modelName === ModelName.CUSTOM_QUERY) {
					const query = model as CustomQuery;
					const metricSetup = query.metricSetup;
					if (metricSetup.metricData) {
						this.selectMetricData(metricSetup.metricData);
					}

					this.trackCreateFromGenericMetric(
						TrackingEvent.QUERY_CREATED,
						metricSetup.sourceType?.key,
					);
				} else if (model.modelName === ModelName.HORIZON_METRIC) {
					const horizonMetric = model as HorizonMetric;
					const metricData = horizonMetric.metricSetup.metricData;
					if (metricData) {
						this.selectMetricData(metricData);
					}

					this.trackCreateFromGenericMetric(
						TrackingEvent.QUERY_CREATED,
						horizonMetric.sourceObj?.sourceType.key,
					);
				} else {
					const metrics = this.mmMetricsService.getMetricsForScriptId(
							(model as Script).numericIdForSure,
						),
						metricData = metrics[0]?.metricData;
					if (metricData) {
						this.selectMetricData(metricData);
					}

					this.trackCreateFromGenericMetric(
						TrackingEvent.CALCULATION_CREATED,
						metrics[0]?.sourceType.key,
					);
				}
			},
		);
	}

	@action
	connectSource(metric: Metric): void {
		void metric.sourceType
			.connectNew({ origin: this.popupData.origin })
			.then((data: onConnectSourceResult) => {
				if (data.isSuccess && isAlive(this)) {
					this.handleResolvingPredefinedMetric(metric, data.source.id);
				}
			});
	}

	@action
	teardownView(): void {
		// cleanup some temporary marked/updated props
		if (this.popupData.disableYouTubeMetrics || this.popupData.disableNotConnected) {
			this.items.map((item) => {
				item.isTemporaryDisabledForMetricSelectPopupYoutube = false;
				if (!item.isPredefinedOnGenericSource) {
					item.isTemporaryDisabledForMetricSelectPopupNotConnected = false;
				}
			});
		}
	}

	private handleResolvingPredefinedMetric(metric: Metric, spaceAccessId: number): void {
		const warningHandled = metric.handleQbOrScriptWarning();
		if (!warningHandled) {
			void this.mmMetricsService
				.resolveGenericMetricWithSpecific(metric.key, spaceAccessId)
				.then((metricSetupData) => {
					this.resolveAndClose(metricSetupData);

					if (metric.isTypeCustomQueryOrGeneric) {
						this.trackCreateFromGenericMetric(TrackingEvent.QUERY_CREATED);
					} else if (metric.isTypeMetricCalculationOrGeneric) {
						this.trackCreateFromGenericMetric(TrackingEvent.CALCULATION_CREATED);
					}
				});
		}
	}

	private resolveAndClose(metricSetupData: DSSetup.MetricSetupData) {
		this.popupData.onChange(metricSetupData);
		this.close();
	}

	private trackCreateFromGenericMetric(trackingEvent: TrackingEvent, sourceTypeKey?: string) {
		const eventName = trackingEvent,
			eventData: APIData.Tracking.Data = {
				origin: 'metric popup',
			};

		if (sourceTypeKey) {
			eventData.datasource = sourceTypeKey;
		}

		this.analyticsService.event(eventName, eventData);
	}
	<template>
		<div
			class={{if this.popupData.disableYouTubeMetrics "disable-youtube"}}
			{{willDestroy this.teardownView}}
		>
			<FilterLineComponent @model={{this.filterSort}} @theme={{@model.theme}} />

			<FilterNoResultsComponent @model={{this.filterSort}} />

			{{#if this.shownItems}}
				<FormMetricSelectPopupListComponent
					@metric={{this.popupData.metric}}
					@options={{this.shownItems}}
					@hideFavoriteBtn={{this.popupData.hideFavoriteBtn}}
					@hideConnectBtn={{this.popupData.hideConnectBtn}}
					@theme={{@model.theme}}
					@onIncrementalLoad={{this.incrementalLoad}}
					@onSelect={{this.selectItem}}
					@originForMetricFavorite={{this.popupData.originForMetricFavorite}}
				/>
				{{#if this.showCreateCustomMetrics}}
					<div class="action-items">
						<div
							role="button"
							class="item item--action"
							{{on "click" this.createCustomMetric}}
						>
							<CommonUiIconComponent @name="Plus" @size="sm" />
							<span>New custom metric</span>
						</div>
					</div>
				{{/if}}
			{{else}}

				{{#unless this.hideNoResultsActions}}
					<div class="actions">
						<SubmitButtonComponent
							@text="New custom metric"
							@icon="Plus"
							@onTrigger={{this.createCustomMetric}}
						/>
					</div>
				{{/unless}}

			{{/if}}

		</div>
	</template>
}
